import React from 'react';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import { useParticipant } from '../../hooks/useParticipant';
import type { PersonWork, RosterMember, RosterResponse } from '../../types/aai';
import {
  Chip, Divided, EmptyState, ErrorNote, ListRow, Loading, Panel, PanelHead, RouteMark,
} from '../../components/aa';

/*
 * The department's people, for the head who runs it.
 *
 * The scope is the server's: /aai/members reads the caller's viewable scope, and
 * a department rollup grant returns that department and no other. What comes
 * back is identity and onboarding status, never a readiness number. Readiness
 * for one person is a different endpoint behind a different grant, and a head
 * does not hold it: they read their team's readiness as an aggregate on AI
 * Adoption, which is the only shape that cannot single anybody out.
 */

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Owner',
  AI_CHAMPION: 'AI Champion',
  DEPARTMENT_HEAD: 'Department head',
  AI_PARTICIPANT: 'Employee',
  TEAM_LEAD: 'Team lead',
  EXECUTIVE: 'Executive',
  TRAINER: 'Trainer',
  EXPERT: 'Expert',
};

const roleOf = (role: string | null | undefined): string => {
  if (!role) return 'No workspace role';
  return ROLE_LABEL[role] || role;
};

const headcount = (n: number): string => {
  if (n === 1) return '1 person';
  return `${n} people`;
};

const startedTone = (onboarded: boolean): 'ok' | 'neutral' => {
  if (onboarded) return 'ok';
  return 'neutral';
};

const startedLabel = (onboarded: boolean): string => {
  if (onboarded) return 'Onboarded';
  return 'Not started';
};

const WorkingOn: React.FC<{ work?: PersonWork }> = ({ work }) => {
  if (!work || work.total === 0) {
    return <span className="text-[12.5px] text-ink-soft">Nothing open right now</span>;
  }
  const shown = work.requests.length + work.skills.length;
  const more = work.total - shown;
  return (
    <span className="flex flex-col gap-1 text-[12.5px] leading-[18px] text-ink-muted">
      {work.requests.length > 0 && (
        <span>
          <span className="font-semibold text-ink">Working on: </span>
          {work.requests.map((r) => `${r.title} (${r.state})`).join(' · ')}
        </span>
      )}
      {work.skills.length > 0 && (
        <span>
          <span className="font-semibold text-ink">Skills they made: </span>
          {work.skills.map((s) => s.name).join(' · ')}
        </span>
      )}
      {more > 0 && <span className="text-ink-soft">{`and ${more} more`}</span>}
    </span>
  );
};

const MemberRow: React.FC<{ m: RosterMember }> = ({ m }) => (
  <ListRow
    mark={<RouteMark kind="adoption" glyph={(m.name || m.email).charAt(0).toUpperCase()} size="lg" />}
    title={m.name || m.email}
    desc={m.email}
    foot={<WorkingOn work={m.working_on} />}
    badge={<Chip tone={startedTone(m.onboarded)} dot>{startedLabel(m.onboarded)}</Chip>}
    meta={[{ label: 'Role', value: roleOf(m.workspace_role) }]}
    metaWidth={140}
  />
);

const NO_DEPARTMENT = 'No department yet';

const byDepartment = (members: RosterMember[]): [string, RosterMember[]][] => {
  const groups = new Map<string, RosterMember[]>();
  members.forEach((m) => {
    const name = m.department || NO_DEPARTMENT;
    groups.set(name, [...(groups.get(name) || []), m]);
  });
  const entries = Array.from(groups.entries());
  return [...entries.filter(([n]) => n !== NO_DEPARTMENT), ...entries.filter(([n]) => n === NO_DEPARTMENT)];
};

export const DepartmentRoster: React.FC = () => {
  const { context } = useParticipant();
  const roster = useAsync<RosterResponse>(() => aaiApi.getMembers(), 'Could not load your team.');

  if (roster.loading) return <Panel><Loading label="Loading your team" /></Panel>;
  if (roster.error || !roster.data) return <ErrorNote message={roster.error || 'Something went wrong.'} />;

  const members = roster.data.members || [];
  const onboarded = members.filter((m) => m.onboarded).length;
  const canViewIndividual = Boolean(context?.capabilities?.view_individual);
  const company = roster.data.scope === 'company';

  if (company) {
    const groups = byDepartment(members);
    return (
      <Panel>
        <PanelHead
          kicker="People"
          title={`${headcount(members.length)} across ${groups.length} ${groups.length === 1 ? 'department' : 'departments'}`}
          desc={`${onboarded} onboarded. What each person is working on: the requests they raised that are still open, and the skills they made that the company uses.`}
        />
        {members.length === 0 && (
          <EmptyState title="Nobody on the roster yet" reason="People appear here once they are invited and placed in a department." />
        )}
        {groups.map(([name, people]) => (
          <div key={name}>
            <p className="m-0 border-t border-line px-5 pb-1 pt-4 text-[12px] font-semibold uppercase tracking-[0.08em] text-ink-soft md:px-6">
              {`${name} · ${headcount(people.length)}`}
            </p>
            <Divided>
              {people.map((m) => <MemberRow key={m.email} m={m} />)}
            </Divided>
          </div>
        ))}
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHead
        kicker="Your team"
        title={`${headcount(members.length)} in ${roster.data.department || 'your department'}`}
        desc={`${onboarded} onboarded. Who is in the department and what each person is working on. Readiness is reported for the team as a whole on AI Adoption, never person by person.`}
      />
      {members.length === 0 && (
        <EmptyState
          title="Nobody on the roster yet"
          reason="People appear here once they are placed in your department. The Owner adds them, or invites them from the team screen."
        />
      )}
      <Divided>
        {members.map((m) => <MemberRow key={m.email} m={m} />)}
      </Divided>
      {canViewIndividual && (
        <p className="m-0 px-5 pb-5 text-[12px] text-ink-soft md:px-6">Opening a person&apos;s readiness is logged for privacy.</p>
      )}
    </Panel>
  );
};

export default DepartmentRoster;
